import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, Select, Space, message, Spin } from 'antd';
import { DownloadOutlined, CopyOutlined } from '@ant-design/icons';
import { Color } from 'molstar/lib/mol-util/color';

const { Option } = Select;

interface MolStarViewerProps {
  pdbData: string;
  title?: string;
  height?: number | string;
  selectedResidues?: {[chainId: string]: number[]};
  onResidueClick?: (chainId: string, residueNumber: number) => void;
  square?: boolean;
  initialRepresentation?: string;
  autoRotate?: boolean;
  minimal?: boolean; // 最小化模式，隐藏标题和控制按钮
  showHoverInfo?: boolean; // 是否显示悬停氨基酸信息
  disableAllMouseEvents?: boolean; // 禁用所有鼠标事件（登录页专用）
  randomInitialRepresentation?: boolean; // 初始化时随机一种显示模式（登录页专用）
}

const MolStarViewer: React.FC<MolStarViewerProps> = ({
  pdbData,
  title,
  height = 500,
  selectedResidues = {},
  onResidueClick,
  square = false,
  initialRepresentation = 'cartoon',
  autoRotate = false,
  minimal = false,
  showHoverInfo = true,
  disableAllMouseEvents = false,
  randomInitialRepresentation = false
}) => {
  // 计算实际高度
  const getActualHeight = () => {
    // 当为正方形模式时，高度由CSS的aspect-ratio控制
    if (square) return 'auto';
    if (height === undefined || height === 'auto') {
      return '100%';
    }
    if (typeof height === 'string') {
      return height;
    }
    return `${height}px`;
  };

  // 映射表示类型到 Mol* 类型名称（支持UI值与Mol*内部值）
  const mapRepresentationType = (repr: string): string => {
    // 传入已是 Mol* 内置类型时直通
    const builtin = new Set(['cartoon', 'backbone', 'ball-and-stick', 'spacefill', 'molecular-surface']);
    if (builtin.has(repr)) return repr;
    // UI 显示值 -> Mol* 类型
    const mapping: { [key: string]: string } = {
      'cartoon': 'cartoon',
      'backbone': 'backbone',
      'ball+stick': 'ball-and-stick',
      'spacefill': 'spacefill',
      'surface': 'molecular-surface'
    };
    return mapping[repr] || 'cartoon';
  };

  // 立即应用选择变化（使用新值而非状态）
  const handleRepresentationChange = async (repr: string) => {
    console.log('切换表示到:', repr);
    setRepresentation(repr);
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const pluginRef = useRef<any>(null);
  const [plugin, setPlugin] = useState<any>(null);
  const [representation, setRepresentation] = useState<string>(initialRepresentation);
  // 颜色方案固定为链着色（不提供UI切换）
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isUnmountingRef = useRef(false);
  const initializedRef = useRef(false);
  const lastRepRef = useRef<string | null>(null);
  const moleculeCenterRef = useRef<any>(null);
  const structureComponentsRef = useRef<{ structureComponent: any } | null>(null);
  const [hoverInfo, setHoverInfo] = useState<string>('');
  const rotationIntervalRef = useRef<any>(null);

  const getRandomInitialRepresentation = () => {
    const reps = ['cartoon', 'backbone', 'ball+stick', 'surface'];
    return reps[Math.floor(Math.random() * reps.length)];
  };

  // 设置相机中心到分子质心
  const setupCameraCenter = async (molstarPlugin: any, structure: any) => {
    try {
      const structureRef = molstarPlugin.state.data.select(structure.ref)[0];
      if (!structureRef?.obj?.data) return;

      const { StructureElement } = await import('molstar/lib/mol-model/structure');
      const { Vec3 } = await import('molstar/lib/mol-math/linear-algebra');
      
      const structureData = structureRef.obj.data;
      
      // 计算分子的几何中心
      const boundary = structureData.boundary;
      const center = Vec3.create(
        (boundary.box.min[0] + boundary.box.max[0]) / 2,
        (boundary.box.min[1] + boundary.box.max[1]) / 2,
        (boundary.box.min[2] + boundary.box.max[2]) / 2
      );
      
      // 保存分子中心供后续使用
      moleculeCenterRef.current = center;
      
      // 设置相机目标到分子中心
      const camera = molstarPlugin.canvas3d?.camera;
      if (camera) {
        // 获取当前相机状态
        const snapshot = camera.getSnapshot();
        
        // 更新相机目标点为分子中心
        camera.setState({
          ...snapshot,
          target: center,
          // 设置合适的视距
          radius: boundary.sphere.radius * 2.5
        });
      }
      
      // 使用focusLoci来确保分子完全在视野内
      const wholeLoci = StructureElement.Loci.all(structureData);
      await molstarPlugin.managers.camera.focusLoci([wholeLoci], {
        durationMs: 0,  // 立即聚焦，不要动画
        extraRadius: 0.2  // 添加少量边距
      });
    } catch (error) {
      console.warn('设置相机中心失败:', error);
      // 备用方案
      await molstarPlugin.managers.camera.reset();
    }
  };

  // 设置自定义鼠标控制
  const setupCustomMouseControls = (molstarPlugin: any) => {
    const canvas = molstarPlugin.canvas3d?.canvas;
    if (!canvas) return;
    
    let isRotating = false;
    let lastX = 0;
    let lastY = 0;
    
    // 移除默认的事件监听器
    const newCanvas = canvas.cloneNode(true);
    canvas.parentNode?.replaceChild(newCanvas, canvas);
    molstarPlugin.canvas3d.canvas = newCanvas;
    
    // 禁用右键菜单
    newCanvas.addEventListener('contextmenu', (e: MouseEvent) => {
      e.preventDefault();
      return false;
    });
    
    // 处理鼠标按下
    newCanvas.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button === 0) { // 左键用于旋转
        isRotating = true;
        lastX = e.clientX;
        lastY = e.clientY;
        newCanvas.style.cursor = 'grabbing';
      }
      e.preventDefault();
    });
    
    // 处理鼠标移动
    newCanvas.addEventListener('mousemove', (e: MouseEvent) => {
      if (!isRotating) return;
      
      const deltaX = e.clientX - lastX;
      const deltaY = e.clientY - lastY;
      
      // 旋转相机
      const camera = molstarPlugin.canvas3d?.camera;
      if (camera && moleculeCenterRef.current) {
        const snapshot = camera.getSnapshot();
        
        // 计算旋转角度（根据鼠标移动距离）
        const rotateSpeed = 0.5;
        const xAngle = deltaY * rotateSpeed * Math.PI / 180;
        const yAngle = deltaX * rotateSpeed * Math.PI / 180;
        
        // 应用旋转，保持目标点不变
        camera.setState({
          ...snapshot,
          target: moleculeCenterRef.current,
          rotation: [
            snapshot.rotation[0] + xAngle,
            snapshot.rotation[1] + yAngle,
            snapshot.rotation[2]
          ]
        });
      }
      
      lastX = e.clientX;
      lastY = e.clientY;
    });
    
    // 处理鼠标释放
    newCanvas.addEventListener('mouseup', () => {
      isRotating = false;
      newCanvas.style.cursor = 'grab';
    });
    
    // 处理鼠标离开画布
    newCanvas.addEventListener('mouseleave', () => {
      isRotating = false;
      newCanvas.style.cursor = 'grab';
    });
    
    // 处理滚轮缩放
    newCanvas.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      const camera = molstarPlugin.canvas3d?.camera;
      if (camera && moleculeCenterRef.current) {
        const snapshot = camera.getSnapshot();
        const factor = e.deltaY > 0 ? 1.1 : 0.9;
        
        // 只改变radius（视距），保持target（目标点）不变
        camera.setState({
          ...snapshot,
          target: moleculeCenterRef.current,
          radius: snapshot.radius * factor
        });
      }
    });
    
    // 设置初始光标
    newCanvas.style.cursor = 'grab';
  };

  // 初始化Mol* viewer
  const initMolStar = async () => {
    try {
      isUnmountingRef.current = false;
      setLoading(true);
      setError(null);

      // 动态导入Mol* 非UI 插件（与结构查看器一致）
      const { PluginContext } = await import('molstar/lib/mol-plugin/context');
      const { DefaultPluginSpec } = await import('molstar/lib/mol-plugin/spec');
      const { PluginConfig } = await import('molstar/lib/mol-plugin/config');

      // 创建插件配置（无UI）
      const spec = DefaultPluginSpec();
      spec.config = [
        [PluginConfig.General.DisableAntialiasing, false],
        [PluginConfig.General.PixelScale, 1],
        [PluginConfig.Viewport.ShowAnimation, false]
      ];

      // 默认禁用所有交互行为，避免点击产生局部球棍/聚焦效果
      // 这样可以保留相机控制（旋转/缩放），但禁用点击交互
      spec.behaviors = [];

      // 创建插件实例
      const molstarPlugin = new PluginContext(spec);
      molstarPlugin.mount(containerRef.current!);
      await molstarPlugin.init();
      
      // 设置基本渲染属性
      try {
        molstarPlugin.canvas3d?.setProps({
          renderer: { 
            backgroundColor: 0xFFFFFF,
            pickingAlphaThreshold: 0.5
          },
          marking: {
            enabled: false as any
          },
          camera: {
            mode: 'perspective' as any, // 使用透视投影以获得自然缩放/旋转体验
            helper: {
              axes: { name: 'off', params: {} }
            }
          },
          trackball: {
            panSpeed: 1,
            rotateSpeed: 1,
            zoomSpeed: 1,
            noRotate: disableAllMouseEvents,
            noPan: disableAllMouseEvents,
            noZoom: disableAllMouseEvents,
            staticMoving: false
          },
          interaction: {
            autoFocus: false,
            focusTimeout: 0
          }
        } as any);
      } catch (e) {
        console.warn('设置canvas3d属性失败:', e);
      }
      
      // 加载PDB数据
      // 检测数据格式（PDB 或 CIF）
      const isCif = pdbData.trim().startsWith('data_') || pdbData.includes('_atom_site.');
      const format = isCif ? 'mmcif' : 'pdb';
      
      console.log(`检测到结构格式: ${format}`);
      
      const data = await molstarPlugin.builders.data.rawData({ 
        data: pdbData, 
        label: 'Protein Complex' 
      });
      
      const trajectory = await molstarPlugin.builders.structure.parseTrajectory(data, format);
      const model = await molstarPlugin.builders.structure.createModel(trajectory, { modelIndex: 0 });
      
      if (!model || !model.cell || !model.cell.obj) {
        throw new Error('无法创建结构模型');
      }
      
      const structure = await molstarPlugin.builders.structure.createStructure(model);
      
      // 计算颜色主题：最小化模式下使用统一配色，其它场景按链着色
      const colorTheme = minimal
        ? { name: 'uniform' as any, params: { value: Color(0xE67E22) } as any }
        : { name: 'chain-id' as any };
      
      // 选择初始表示：若启用随机初始表示，则随机选择一种
      const initialReprValue = randomInitialRepresentation ? getRandomInitialRepresentation() : representation;
      if (randomInitialRepresentation) {
        // 同步内部状态，后续切换/重载保持一致
        setRepresentation(initialReprValue);
      }
      
      // 添加默认表示
      const molstarType = mapRepresentationType(initialReprValue);
      console.log('初始添加表示:', molstarType, 'colorTheme:', minimal ? 'uniform' : 'chain-id');
      const repResult = await molstarPlugin.builders.structure.representation.addRepresentation(structure, {
        type: molstarType as any,
        colorTheme
      });
      const initialRep = (repResult as any)?.representation;
      const components = (repResult as any)?.components;
      structureComponentsRef.current = components;
      try {
        lastRepRef.current = (initialRep as any)?.transform?.ref ?? null;
      } catch {}
      
      // 设置相机中心到分子
      await setupCameraCenter(molstarPlugin, structure);
      
      // 注释掉自定义鼠标控制，使用默认的trackball控制
      // setupCustomMouseControls(molstarPlugin);

      // 如果需要彻底禁用鼠标事件，禁用 canvas 的 pointer-events
      if (disableAllMouseEvents) {
        try {
          const canvasEl = (molstarPlugin.canvas3d as any)?.canvas as HTMLCanvasElement | undefined;
          if (canvasEl) canvasEl.style.pointerEvents = 'none';
        } catch {}
      }
      
      // 如果启用自动旋转
      if (autoRotate) {
        console.log('启动自动旋转...');
        // 延迟一下确保相机已经设置好
        setTimeout(() => {
          startAutoRotation(molstarPlugin);
        }, 500);
      }

      // 悬浮显示链与残基信息（与 PRODIGY 一致）
      if (showHoverInfo) {
        try {
          molstarPlugin.behaviors.interaction.hover.subscribe(async (event: any) => {
            try {
              const loci = event?.current?.loci;
              // 增加更严格的检查：确保 loci 存在且有 kind 属性
              if (!loci || typeof loci !== 'object' || !loci.kind || loci.kind !== 'element-loci' || !loci.elements || loci.elements.length === 0) {
                setHoverInfo('');
                return;
              }

              const { StructureElement, StructureProperties } = await import('molstar/lib/mol-model/structure');
              const first = StructureElement.Loci.getFirstLocation(loci as any);
              if (!first) {
                setHoverInfo('');
                return;
              }

              const authId = StructureProperties.chain.auth_asym_id(first);
              const labelId = StructureProperties.chain.label_asym_id(first);
              const chainId = authId || labelId;

              const resName = StructureProperties.atom.label_comp_id(first);
              const authSeq = StructureProperties.residue.auth_seq_id(first);
              const labelSeq = StructureProperties.residue.label_seq_id(first);
              const resNo = authSeq || labelSeq;

              const info = chainId && resName ? `链: ${chainId} | 氨基酸: ${resName}${resNo ? resNo : ''}` : '';
              setHoverInfo(info);
            } catch (e) {
              setHoverInfo('');
            }
          });
        } catch (e) {
          console.warn('绑定悬浮事件失败:', e);
        }
      }
      
      // 强制渲染
      await molstarPlugin.canvas3d?.requestDraw();
      
      pluginRef.current = molstarPlugin;
      setPlugin({ instance: molstarPlugin, structure });
      initializedRef.current = true;
      setLoading(false);
      
      console.log('Mol* viewer初始化成功');
    } catch (error) {
      console.error('Mol* viewer初始化失败:', error);
      setError(`Mol* viewer初始化失败: ${error}`);
      setLoading(false);
    }
  };


  // 高亮选中的残基
  const highlightSelectedResidues = async (molstarPlugin: any) => {
    if (!molstarPlugin || !plugin?.structure) return;
    try {
      if (!molstarPlugin?.managers?.structure?.component?.update || !structureComponentsRef.current?.structureComponent) {
        return;
      }
      const structureQueryModule = await import('molstar/lib/mol-plugin-state/helpers/structure-query');
      const StructureSelectionQuery = (structureQueryModule as any).StructureSelectionQuery || structureQueryModule.default;
      const StructureSelectionQueryBuilder = (structureQueryModule as any).StructureSelectionQueryBuilder;
      const { Color } = await import('molstar/lib/mol-util/color');

      const overpaint = molstarPlugin.managers.structure.component.update.to(structureComponentsRef.current?.structureComponent);
      overpaint.setDefault({ alpha: 0, type: 'regular' });

      const selections: any[] = [];
      for (const [chainId, residues] of Object.entries(selectedResidues)) {
        if (!residues?.length) continue;
        const query = StructureSelectionQuery.Builder.build({
          label: `residues-${chainId}`,
          description: 'Selected residues',
          query: StructureSelectionQueryBuilder.union(residues.map((resi) =>
            StructureSelectionQueryBuilder.struct.generator.atomGroups({
              chainTest: StructureSelectionQueryBuilder.core.rel.eq([StructureSelectionQueryBuilder.core.set.atomProperties.chain.auth_asym_id(), chainId]),
              residueTest: StructureSelectionQueryBuilder.core.rel.eq([StructureSelectionQueryBuilder.core.set.atomProperties.residue.auth_seq_id(), resi])
            })
          ))
        })(molstarPlugin, plugin.structure.data);
        selections.push(query);
      }

      if (selections.length) {
        overpaint.add(selections, Color(0x0066ff), 0.7);
      }
      await molstarPlugin.managers.structure.component.update.commit();
    } catch (error) {
      console.warn('高亮选中残基失败:', error);
    }
  };

  // 更新表示的方法
  const updateRepresentation = async (molstarPlugin: any, structure: any, repr: string) => {
    if (!molstarPlugin || !structure) {
      console.warn('updateRepresentation: plugin或structure为空');
      return;
    }
    try {
      console.log('updateRepresentation 开始:', { repr });

      // 直接使用传入类型（外部已完成映射）
      const molstarType = repr;

      // 与结构查看器一致：完全清空后重建结构与表示，避免叠加
      try {
        await molstarPlugin.clear();
      } catch (e) {
        console.warn('plugin.clear() 失败，继续重建:', e);
      }

      // 重新加载数据并创建新结构
      const isCif = pdbData.trim().startsWith('data_') || pdbData.includes('_atom_site.');
      const format = isCif ? 'mmcif' : 'pdb';
      const data = await molstarPlugin.builders.data.rawData({ data: pdbData, label: 'Protein Complex' });
      const trajectory = await molstarPlugin.builders.structure.parseTrajectory(data, format);
      const model = await molstarPlugin.builders.structure.createModel(trajectory, { modelIndex: 0 });
      if (!model || !model.cell || !model.cell.obj) throw new Error('无法创建结构模型');
      const newStructure = await molstarPlugin.builders.structure.createStructure(model);

      // 计算颜色主题
      const colorTheme = minimal
        ? { name: 'uniform' as any, params: { value: Color(0xE67E22) } as any }
        : { name: 'chain-id' as any };

      // 添加新的表示（仅一个）
      console.log('添加新表示:', molstarType, 'colorTheme:', minimal ? 'uniform' : 'chain-id');
      const repResult = await molstarPlugin.builders.structure.representation.addRepresentation(newStructure, {
        type: molstarType as any,
        colorTheme
      });
      const newRef = (repResult as any)?.representation?.transform?.ref;
      if (newRef) lastRepRef.current = newRef;
      structureComponentsRef.current = (repResult as any)?.components || null;

      // 更新当前结构引用
      setPlugin({ instance: molstarPlugin, structure: newStructure });

      await molstarPlugin.canvas3d?.requestDraw();
      console.log('updateRepresentation 完成');
    } catch (error) {
      console.error('更新Mol*表示失败，详细错误:', error);
      throw error;
    }
  };

  // 注意：颜色方案UI已移除，内部固定为 chain-id

  // 初始化Mol* viewer
  useEffect(() => {
    if (!containerRef.current || !pdbData) return;
    if (initializedRef.current) return;

    initMolStar();

    return () => {
      isUnmountingRef.current = true;
      initializedRef.current = false;
      moleculeCenterRef.current = null;
      try {
        pluginRef.current?.dispose?.();
      } catch (e) {
        console.warn('释放 Mol* 插件时出错:', e);
      } finally {
        pluginRef.current = null;
      }
      setPlugin(null);
    };
  }, [pdbData]);

  // 更新表示
  useEffect(() => {
    console.log('useEffect 触发 - 表示更新:', { representation, hasPlugin: !!plugin, loading });
    
    if (plugin?.instance && plugin?.structure && !loading && !isUnmountingRef.current) {
      console.log('开始执行表示更新...');
      
      updateRepresentation(plugin.instance, plugin.structure, mapRepresentationType(representation))
        .then(() => {
          if (Object.keys(selectedResidues).some(chain => selectedResidues[chain].length > 0)) {
            highlightSelectedResidues(plugin.instance);
          }
          // 如果启用了自动旋转，重新启动（因为 updateRepresentation 会 clear 插件）
          if (autoRotate && plugin.instance) {
            console.log('表示更新后重新启动自动旋转');
            stopAutoRotation(); // 先停止旧的
            setTimeout(() => {
              startAutoRotation(plugin.instance);
            }, 100);
          }
        })
        .catch(error => {
          if (!isUnmountingRef.current) {
            console.error('更新表示失败:', error);
            message.error('更新显示样式失败');
          }
        });
    }
  }, [representation]);

  // 当选中残基变化时更新高亮
  useEffect(() => {
    if (plugin?.instance && plugin?.structure && !loading && !isUnmountingRef.current) {
      highlightSelectedResidues(plugin.instance);
    }
  }, [selectedResidues]);

  // 当 pdbData 在初始化后发生变化时，重新构建结构
  useEffect(() => {
    const reloadStructure = async () => {
      if (!plugin?.instance || !pdbData || !initializedRef.current) return;
      const molstarPlugin = plugin.instance;
      try {
        isUnmountingRef.current = false;
        setLoading(true);

        // 保存当前相机状态
        const cameraSnapshot = molstarPlugin.canvas3d?.camera?.getSnapshot();

        // 移除现有所有表示
        const state = molstarPlugin.state.data;
        const repNodes: any[] = [];
        
        state.tree.root.children.forEach((child: any) => {
          const obj = state.cells.get(child.ref)?.obj;
          if (obj?.type?.name === 'structure-representation-3d') {
            repNodes.push(child.ref);
          }
        });
        
        for (const ref of repNodes) {
          await state.remove(ref);
        }
        if (repNodes.length > 0) {
          await state.commit();
        }

        // 检测数据格式
        const isCif = pdbData.trim().startsWith('data_') || pdbData.includes('_atom_site.');
        const format = isCif ? 'mmcif' : 'pdb';
        
        // 重新解析并创建新的结构
        const data = await molstarPlugin.builders.data.rawData({ data: pdbData, label: 'ZDOCK Structure' });
        const trajectory = await molstarPlugin.builders.structure.parseTrajectory(data, format);
        const model = await molstarPlugin.builders.structure.createModel(trajectory, { modelIndex: 0 });
        
        if (!model || !model.cell || !model.cell.obj) {
          throw new Error('无法创建结构模型');
        }
        
        const structure = await molstarPlugin.builders.structure.createStructure(model);

        // 记录为当前结构
        setPlugin({ instance: molstarPlugin, structure });

        // 应用当前表示与颜色
        const molstarType = mapRepresentationType(representation);
        const colorThemeReload = minimal
          ? { name: 'uniform' as any, params: { value: Color(0xE67E22) } as any }
          : { name: 'chain-id' as any };
        console.log('重新加载时添加表示:', molstarType, 'colorTheme:', minimal ? 'uniform' : 'chain-id');
        const newRep = await molstarPlugin.builders.structure.representation.addRepresentation(structure, {
          type: molstarType as any,
          colorTheme: colorThemeReload
        });
        try {
          lastRepRef.current = (newRep as any)?.transform?.ref ?? null;
        } catch {}
        
        // 重新设置相机中心
        await setupCameraCenter(molstarPlugin, structure);
        
        // 如果有保存的相机状态，恢复它
        if (cameraSnapshot && moleculeCenterRef.current) {
          molstarPlugin.canvas3d?.camera?.setState({
            ...cameraSnapshot,
            target: moleculeCenterRef.current
          });
        }
        
        // 注释掉自定义鼠标控制，使用默认的trackball控制
        // setupCustomMouseControls(molstarPlugin);
        
        // 重新高亮选中的残基
        if (Object.keys(selectedResidues).some(chain => selectedResidues[chain].length > 0)) {
          await highlightSelectedResidues(molstarPlugin);
        }

        // 重绘
        await molstarPlugin.canvas3d?.requestDraw();
      } catch (e) {
        console.error('重新加载结构失败:', e);
      } finally {
        setLoading(false);
      }
    };

    reloadStructure();
  }, [pdbData]);


  // 启动自动旋转
  const startAutoRotation = (molstarPlugin: any) => {
    if (rotationIntervalRef.current) {
      cancelAnimationFrame(rotationIntervalRef.current);
    }
    
    console.log('自动旋转已启动');
    
    const rotate = () => {
      try {
        if (!molstarPlugin?.canvas3d?.camera) {
          return;
        }
        
        const camera = molstarPlugin.canvas3d.camera;
        const state = camera.state;
        
        // 获取当前相机位置和目标点
        const position = state.position;
        const target = state.target;
        const up = state.up;
        
        // 计算相机相对于目标点的位置
        const dx = position[0] - target[0];
        const dy = position[1] - target[1];
        const dz = position[2] - target[2];
        
        // 计算当前角度和半径
        const radius = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const angleY = Math.atan2(dx, dz);
        
        // 增加角度（绕Y轴旋转）
        const rotationSpeed = 0.01; // 每帧旋转角度
        const newAngleY = angleY + rotationSpeed;
        
        // 计算新的相机位置
        const newX = target[0] + radius * Math.sin(newAngleY);
        const newZ = target[2] + radius * Math.cos(newAngleY);
        
        // 更新相机状态
        camera.setState({
          position: [newX, position[1], newZ],
          target: target,
          up: up
        }, 0);
        
        // 强制重绘
        molstarPlugin.canvas3d.requestDraw();
      } catch (error) {
        console.error('旋转失败:', error);
        // 停止动画以防止无限错误
        stopAutoRotation();
      }
    };
    
    // 使用 requestAnimationFrame 实现流畅动画
    const animate = () => {
      rotate();
      rotationIntervalRef.current = requestAnimationFrame(animate);
    };
    
    rotationIntervalRef.current = requestAnimationFrame(animate);
  };
  
  // 停止自动旋转
  const stopAutoRotation = () => {
    if (rotationIntervalRef.current) {
      cancelAnimationFrame(rotationIntervalRef.current);
      rotationIntervalRef.current = null;
      console.log('自动旋转已停止');
    }
  };

  // 组件卸载时的清理
  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
      moleculeCenterRef.current = null;
      stopAutoRotation();
    };
  }, []);
  
  // 监听autoRotate变化
  useEffect(() => {
    if (plugin?.instance) {
      if (autoRotate) {
        startAutoRotation(plugin.instance);
      } else {
        stopAutoRotation();
      }
    }
  }, [autoRotate, plugin]);

  // 下载PDB文件
  const handleDownloadPDB = () => {
    if (!pdbData) {
      message.error('没有可下载的结构数据');
      return;
    }
    
    const blob = new Blob([pdbData], { type: 'chemical/x-pdb' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = title ? `${title}.pdb` : '结构.pdb';
    a.style.display = 'none';
    
    document.body.appendChild(a);
    a.click();
    
    setTimeout(() => {
      try {
        if (a.parentNode === document.body) {
          document.body.removeChild(a);
        }
        URL.revokeObjectURL(url);
      } catch (error) {
        console.warn('清理下载元素时出错:', error);
        URL.revokeObjectURL(url);
      }
    }, 100);
    
    message.success('PDB文件下载已开始');
  };

  // 复制PDB数据
  const handleCopyPDB = () => {
    if (!pdbData) {
      message.error('没有可复制的PDB数据');
      return;
    }
    
    navigator.clipboard.writeText(pdbData)
      .then(() => {
        message.success('PDB数据已复制到剪贴板');
      })
      .catch(() => {
        message.error('复制失败，请手动复制');
      });
  };

  if (error) {
    return (
      <Card title={title || 'Mol* 结构查看器'}>
        <div style={{ 
          height: getActualHeight(),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#ff4d4f',
          backgroundColor: '#fff2f0',
          border: '1px solid #ffccc7',
          borderRadius: '6px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div>Mol* 加载失败</div>
            <div style={{ fontSize: '12px', marginTop: '8px' }}>{error}</div>
          </div>
        </div>
      </Card>
    );
  }

  // 最小化模式：只显示查看器，不显示卡片和控制
  if (minimal) {
    return (
      <div 
        ref={containerRef}
        style={{ 
          width: '100%', 
          height: getActualHeight(),
          ...(square ? ({ aspectRatio: '1 / 1' } as any) : {}),
          border: 'none',
          borderRadius: '0',
          backgroundColor: loading ? '#f5f5f5' : 'transparent',
          position: 'relative',
          overflow: 'hidden',
          flex: height === 'auto' ? 1 : undefined
        }}
        onContextMenu={(e) => { e.preventDefault(); }}
      >
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1000
          }}>
            <Space direction="vertical" align="center">
              <Spin size="large" />
              <div style={{ color: '#666' }}>正在加载Mol*查看器...</div>
            </Space>
          </div>
        )}
        {hoverInfo && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              padding: '6px 10px',
              background: 'rgba(0,0,0,0.75)',
              color: '#fff',
              borderRadius: 4,
              fontSize: 12,
              fontFamily: 'monospace',
              zIndex: 1000,
              pointerEvents: 'none'
            }}
          >
            {hoverInfo}
          </div>
        )}
      </div>
    );
  }

  return (
    <Card 
      style={{ 
        height: height === 'auto' ? '100%' : undefined,
        display: height === 'auto' ? 'flex' : undefined,
        flexDirection: height === 'auto' ? 'column' : undefined
      }}
      styles={{
        body: {
          flex: height === 'auto' ? 1 : undefined,
          padding: '16px',
          display: height === 'auto' ? 'flex' : undefined,
          flexDirection: height === 'auto' ? 'column' : undefined
        }
      }}
      title={
        <Space>
          <span>{title || 'Mol* 结构查看器'}</span>
          {loading && <Spin size="small" />}
        </Space>
      }
      extra={
        <Space>
          <Select
            value={representation}
            onChange={handleRepresentationChange}
            style={{ width: 120 }}
            disabled={loading}
          >
            <Option value="cartoon">Cartoon</Option>
            <Option value="backbone">Backbone</Option>
            <Option value="ball+stick">Ball & Stick</Option>
            <Option value="spacefill">Spacefill</Option>
            <Option value="surface">Surface</Option>
          </Select>
          
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDB}
            disabled={loading}
          >
            下载PDB
          </Button>
          
          <Button
            icon={<CopyOutlined />}
            onClick={handleCopyPDB}
            disabled={loading}
          >
            复制PDB
          </Button>
        </Space>
      }
    >
      <div 
        ref={containerRef}
        style={{ 
          width: '100%', 
          height: getActualHeight(),
          ...(square ? ({ aspectRatio: '1 / 1' } as any) : {}),
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          backgroundColor: loading ? '#f5f5f5' : 'white',
          position: 'relative',
          overflow: 'hidden',
          flex: height === 'auto' ? 1 : undefined
        }}
        onContextMenu={(e) => { e.preventDefault(); }}
      >
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1000
          }}>
            <Space direction="vertical" align="center">
              <Spin size="large" />
              <div style={{ color: '#666' }}>正在加载Mol*查看器...</div>
            </Space>
          </div>
        )}
        {hoverInfo && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              padding: '6px 10px',
              background: 'rgba(0,0,0,0.75)',
              color: '#fff',
              borderRadius: 4,
              fontSize: 12,
              fontFamily: 'monospace',
              zIndex: 1000,
              pointerEvents: 'none'
            }}
          >
            {hoverInfo}
          </div>
        )}
      </div>
      
      <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
        提示: 使用鼠标左键拖拽旋转，滚轮缩放
      </div>
    </Card>
  );
};

export default MolStarViewer;